import Box, { type BoxProps } from '@mui/material/Box';
import { TextField } from '@openx/components-core/lib/TextField/TextField';
import {
  type ContextualSegmentsDataPayload,
  dropdownOptions,
  segmentIdMaxLength,
  segmentIdsLimit,
  segmentIdsPlaceholder,
  useContextualSegmentsDropdownLabels,
} from '@openx/types/targeting/contextualSegments';
import type { SegmentsOption } from '@openx/types/targeting/customVariables';
import { ComparisonType, type CriteriaValue } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { type ChangeEvent, type JSX, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { removeNotAllowedSymbols } from '../../../ContextualSegments/helpers';
import { getContextualSegmentKey, normalizeProviderId } from '../../../ContextualSegments/utils/enrichment';
import { ComparisonTypeDropdown } from '../../../shared/ComparisonTypeDropdown/ComparisonTypeDropdown';
import { DimensionChip } from '../../../shared/DimensionChip/DimensionChip';
import { SelectedItems } from '../../../shared/SelectedItems/SelectedItems';

const segmentIdsSplitter = /\n|,|;/;

const StyledHelperTextBox = (props: BoxProps) => <Box component="span" data-test="helper-text" {...props} />;

interface Props {
  item: ContextualSegmentsDataPayload;
  onChange?: (ctxSegment: ContextualSegmentsDataPayload) => void;
  targetingDimension: CriteriaValue;
  operatorSwitch?: JSX.Element;
  readonly?: boolean;
  segmentsMap?: Record<string, SegmentsOption>;
  providerNamesMap?: Record<string, string>;
}

export const SegmentIdsItems = ({
  item,
  operatorSwitch,
  targetingDimension,
  readonly = true,
  onChange = noop,
  segmentsMap = {},
  providerNamesMap = {},
}: Props) => {
  const { t } = useTranslation();
  const labels = useContextualSegmentsDropdownLabels();
  const [comparisonType, setComparisonType] = useState<ComparisonType>(dropdownOptions[0]);

  const [segmentIds, setSegmentIds] = useState(item.includes.replaceAll(',', '\n'));
  const [segmentIdsError, setSegmentIdsError] = useState<string | null>(null);

  const handleRemove = (segments: SegmentsOption[]) => {
    const segmentIds = segments.map(s => s.id);
    setSegmentIds(segmentIds.join('\n'));

    const segmentsStr = segmentIds.join(',');
    onChange({
      attr: item.attr,
      excludes: comparisonType === ComparisonType.NOT_INTERSECTS ? segmentsStr : item.excludes,
      includes: comparisonType === ComparisonType.INTERSECTS ? segmentsStr : item.includes,
    });
  };

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const segmentIdsInput = event.target.value;

      let items = [
        ...new Set(
          segmentIdsInput
            .split(segmentIdsSplitter)
            .map(value => removeNotAllowedSymbols(value))
            .filter(value => !!value)
        ),
      ];

      const hasElementsWithEceededMaxLength = items.some(item => item.length > segmentIdMaxLength);
      if (hasElementsWithEceededMaxLength) {
        setSegmentIdsError(t('Length of each ID cannot exceed {limit} characters.', { limit: segmentIdMaxLength }));
        return;
      }

      if (items.length > segmentIdsLimit) {
        setSegmentIdsError(t("Number of segment IDs can't be higher than {limit}.", { limit: segmentIdsLimit }));
        items = items.slice(0, segmentIdsLimit);
      } else {
        setSegmentIdsError('');
      }

      setSegmentIds(segmentIdsInput);

      const segments = items.join(',');
      onChange({
        attr: `${item.attr}`,
        excludes: comparisonType === ComparisonType.NOT_INTERSECTS ? segments : item.excludes,
        includes: comparisonType === ComparisonType.INTERSECTS ? segments : item.includes,
      });
    },
    [t, onChange, comparisonType, item]
  );

  const excludedItems = item.excludes.split(',').filter(Boolean);
  const includedItems = item.includes.split(',').filter(Boolean);
  const shouldShowDimension = item.excludes && item.includes;
  const providerId = normalizeProviderId(item.attr);

  // Build corrected segment objects with provider-scoped lookup and metadata override
  const buildCorrectedSegment = useCallback(
    (segmentId: string): SegmentsOption => {
      const lookupKey = getContextualSegmentKey({ providerId, segmentId });
      const foundSegment = segmentsMap[lookupKey];
      const fallbackProviderName = providerNamesMap[providerId] || providerId;

      if (foundSegment) {
        // Use segment from catalog but override provider info to prevent corrupted data
        return {
          ...foundSegment,
          provider_name: fallbackProviderName,
          contextual_provider: providerId,
        };
      }

      // Fallback: create segment with ID as name and correct provider
      return {
        id: segmentId,
        name: segmentId,
        provider_name: fallbackProviderName,
        contextual_provider: providerId,
      };
    },
    [providerId, segmentsMap, providerNamesMap]
  );

  const correctedIncludedSegments = includedItems.map(buildCorrectedSegment);
  const correctedExcludedSegments = excludedItems.map(buildCorrectedSegment);

  // Build segment display labels using provider-scoped lookup
  const getSegmentLabel = useCallback(
    (segment: SegmentsOption) => {
      const displayName = segment.name || segment.id;
      const providerLabel = segment.provider_name || item.attr;
      return `${displayName} (${providerLabel})`;
    },
    [item.attr]
  );

  return (
    <Box
      data-test="provider-segment-ids"
      sx={{
        marginTop: 1,
      }}
    >
      {!readonly && (
        <>
          <ComparisonTypeDropdown
            dataTest="contextual-segments-comparison-options"
            labels={labels}
            options={dropdownOptions}
            value={comparisonType}
            sx={{ marginBottom: 1, marginTop: 0 }}
            onChange={(value: ComparisonType) => {
              setComparisonType(value);
              const segments = value === ComparisonType.NOT_INTERSECTS ? item.excludes : item.includes;
              setSegmentIds(segments.replaceAll(',', '\n'));
            }}
          />
          <Box
            sx={{
              marginBottom: 1,
            }}
          >
            <TextField
              data-test="contextual-segments-ids-area-input"
              error={!!segmentIdsError}
              helperText={<StyledHelperTextBox>{segmentIdsError}</StyledHelperTextBox>}
              slotProps={{ input: { disableUnderline: true, inputComponent: 'textarea' } }}
              label={t('Segment IDs')}
              rows={9}
              placeholder={t(segmentIdsPlaceholder)}
              value={segmentIds}
              onChange={handleOnChange}
              fullWidth
              multiline
            />
          </Box>
          {shouldShowDimension && operatorSwitch}
        </>
      )}

      {item.includes && (
        <SelectedItems
          dataTest="contains-any-of-segment-ids"
          filtersTitle={t('Contains any of following Segment IDs')}
          selectedItems={correctedIncludedSegments}
          readonly={comparisonType !== ComparisonType.INTERSECTS || readonly}
          handleChange={handleRemove}
          getItemLabel={getSegmentLabel}
          isAddIcon
        />
      )}

      {shouldShowDimension && (
        <DimensionChip targetingDimension={targetingDimension} sx={{ marginBottom: 1, marginLeft: 0, marginTop: 1 }} />
      )}

      {item.excludes && (
        <SelectedItems
          dataTest="contains-none-of-segment-ids"
          filtersTitle={t('Contains none of following Segment IDs')}
          selectedItems={correctedExcludedSegments}
          readonly={comparisonType !== ComparisonType.NOT_INTERSECTS || readonly}
          handleChange={handleRemove}
          getItemLabel={getSegmentLabel}
          isAddIcon={false}
        />
      )}
    </Box>
  );
};
